import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import FormComponent from '../FormComponent';
import {
	addInvalidClasses,
	InputTypes,
	removeInvalidClasses,
	validFormElementValue
} from '../../utils/validateFormElement';
import { Events } from '../../utils/dom/html-events';
import { throttle } from 'lodash-es';
import { checkInputAvailability } from '../../utils/inputAvailabilityCheck';

const EVENT_KEY_COUNTER = `.lta.textareacounter`;
const EVENT_KEYUP_COUNTER = `keyup.${EVENT_KEY_COUNTER}`;
class InputField extends FormComponent {
	init(questionData) {
		this.renderInputField(questionData);
	}
	markup(value, data = {}, titleMarkup) {
		this.inputFieldContainerClass = `lta-input-field-container-${data.id}`;
		this.inputID = `lta-input-field-id-${data.id}`;
		const placeholderText = data.placeHolder || '';
		const inputValue = data.value || '';
		const inputType = data.type || 'text';
		const inputName = `lta-input-field-name-${data.id}`;
		this.hasCounter = data.hasOwnProperty('hasCounter') ? data?.hasCounter : true;
		return `<div class="lta-input-field">
					<div class="lta-input-field-title">${titleMarkup}</div>
					<div class="lta-text-input__wrapper ${this.inputFieldContainerClass}">
						${this.renderInstructionLabel(data.instructionText)}
						${this.data.toolTip
							? this.renderLabelWithTooltip(this.inputID, this.data.toolTip)
							: this.renderLabel(this.inputID)
						}
						${this.renderDescription(this.inputID)}
						<div class="lta-text-input--error"></div>
						${
							data?.isTextArea
								? `
								<textarea 
									type="${inputType}"
									class="lta-text-input u-mb-1" 
									id="${this.inputID}"
									name="${inputName}" 
									placeholder="${placeholderText}"
									rows="${data.rows || ''}"
									maxlength="${data?.maxlength}"
									minlength="${data?.minlength}"
									>${inputValue.length > 0 ? inputValue : ''}</textarea>
								`
								: this.renderTextInput({
										inputType,
										inputName,
										placeholderText,
										inputValue,
										data
								  })
						}
						${
							data?.isTextArea && this.hasCounter
								? `<div class="lta-text-input__counter u-mb-3 u-text-left">
										<span id="textarea-counter__current">${data?.maxlength ? data.maxlength : data.charLimit}</span> characters remaining
										<span class="u-hidden" id="textarea-counter__maximum">${data?.maxlength ? data.maxlength : data.charLimit}</span>
									</div>`
								: ``
						}
					</div>
				</div>`;
	}

	renderLabel(inputID) {
		return this.data.titlePattern
			? ``
			: `<label for=${inputID} class="lta-text-input__label u-text-grey-lta">${this.data.label}</label>`;
	}

	renderDescription(inputID) {
		return this.data.description
			? `<div class="u-mb-1"><label for=${inputID} class="lta-text-input__description u-text-grey-lta">${this.data.description}</label></div>`
			: ``;
	}
	
	renderLabelWithTooltip(inputID, toolTip) {
		return this.data.titlePattern
			? ``
			: `<div class="u-caption2 js-lta-tooltip u-pb-1 u-pr-1">
					<div class="sm:u-flex md:u-flex xl:u-hstack js-lta-tooltip__trigger lta-cookies-modal-title2" tabindex="0">
						<label for=${inputID} class="lta-text-input__label u-text-grey-lta">${this.data.label}</label>
						<svg class="fe-icon u-ml-1" width="1.5em" height="1.5em" fill="currentColor" style="flex-shrink:0;">
							<use xlink:href="/static/images/icons/lta-icons.svg#info"></use>
						</svg>
					</div>
					<div class="js-lta-tooltip__content lta-cookies-modal-text u-body1 u-pl-2">
						${toolTip}
					</div>
				</div>`;
	}

	renderTextInput({ inputType, inputName, placeholderText, inputValue, data }) {
		const prefix = data?.prefix;
		const digitsOnly = Boolean(data?.digitsOnly);
		const inputMarkup = `
								<input 
									type="${inputType}"
									class="lta-text-input${prefix ? '' : ' u-mb-3'}" 
									id="${this.inputID}"
									name="${inputName}" 
									placeholder="${placeholderText}"
									value="${inputValue}"
									${data?.disable ? `disabled` : ''}
									${digitsOnly ? 'inputmode="numeric" pattern="[0-9]*" autocomplete="off"' : ''}
								/>`;

		if (!prefix) {
			return inputMarkup;
		}

		return `
						<div class="lta-text-input__icon-btn-wrapper u-mb-3">
							<span class="lta-text-input__prefix" aria-hidden="true">${prefix}</span>
							${inputMarkup}
						</div>`;
	}

	renderInstructionLabel(instructionLabel) {
		return instructionLabel
			? `<div class="u-mb-2">
					<span class="lta-text-input__label-instruction ${this.data?.instructionTextStyleClss || ''}">
			  			${instructionLabel}
					</span>
				</div>`
			: '';
	}

	afterRender() {
		this.componentElement = SelectorEngine.findOne('.lta-input-field', this._element);
		this.inputFieldContainerElement = SelectorEngine.findOne(`.${this.inputFieldContainerClass}`, this._element);
		this.inputFieldElement = SelectorEngine.findOne(`#${this.inputID}`, this.inputFieldContainerElement);
		this.toolTipTrigger = SelectorEngine.findOne('.js-lta-tooltip__trigger', this._element);

		if (this.toolTipTrigger) {
			EventHandler.on(this.toolTipTrigger, 'click', (event) => {
				this.toolTipHanlder(event);
			});
		}

		EventHandler.on(this.toolTipTrigger, 'keypress', (event) => {
			if (event.keyCode == 13) this.toolTipHanlder(event);
		});

		if (this.data?.digitsOnly && this.inputFieldElement) {
			this.sanitizeDigitsOnly();
			EventHandler.on(this.inputFieldElement, 'input', () => {
				this.sanitizeDigitsOnly();
			});
		}

		if (this.data?.value) {
			setTimeout(() => {
				this.handleInput();
			}, 100);
		}

		const textareaEl = SelectorEngine.findOne('textarea', this._element);

		this.counterContentUpdate();

		EventHandler.on(
			textareaEl,
			EVENT_KEYUP_COUNTER,
			throttle(() => {
				this.counterContentUpdate();
			}, 200)
		);

		// Reset counter on unmount
		this.lastCheckedEmail = null;
		this.lastEmailCheckResult = null;

		EventHandler.on(this.inputFieldElement, Events.Blur, () => {
			this.handleInput();

			// Only check for duplicate email if the input type is email and duplicateEmailCheck flag is true..
			// To avoid unnecessary API calls for other input types
			if (this.data?.type === 'email' && this.data?.duplicateEmailCheck) {
				this.checkEmailUnique();
			}
		});
	}

	toolTipHanlder() {
		const content = this.toolTipTrigger?.nextElementSibling;

		if (content) {
			content.classList.toggle('is-visible');
		}
	}

	counterContentUpdate() {
		if (!this.hasCounter) {
			return;
		}
		const limit = this.data?.maxlength ? this.data?.maxlength : this.data?.minlength;

		const textareaEl = SelectorEngine.findOne('textarea', this._element);
		const currentValueEl = SelectorEngine.findOne('#textarea-counter__current', this._element);

		if (textareaEl?.value?.length >= 0) {
			if (this.data?.maxlength === (null || undefined || '') || !this.data?.maxlength) {
				currentValueEl.textContent = limit - textareaEl.value.length >= 0 ? limit - textareaEl.value.length : 0;
			} else {
				currentValueEl.textContent = limit - textareaEl.value.length;
			}
		}
	}

	sanitizeDigitsOnly() {
		if (!this.inputFieldElement) {
			return;
		}

		const originalValue = this.inputFieldElement.value;
		const sanitizedValue = originalValue.replace(/[^0-9]/g, '');
		if (originalValue !== sanitizedValue) {
			this.inputFieldElement.value = sanitizedValue;
		}
	}

	handleInput() {
		const trimmedValue = this.getTrimmedValue();
		this.inputValue = trimmedValue;
		this.inputFieldElement.value = trimmedValue;
		this.validate();

		this.value = trimmedValue ? { ...this.data, value: trimmedValue } : { ...this.data, value: undefined };
		if (this.value) {
			this.dependentQuestion = this.value.dependentQuestion;
		}
	}

	getTrimmedValue() {
		const rawValue = this.inputFieldElement?.value;
		return typeof rawValue === 'string' ? rawValue.trim() : rawValue || '';
	}

	// Implemented for USER STORY 34063 - Hypercare - Add Email Validation During Competition Details form
	// This function checks if the email already exists in the system by calling the API. 
	// It also handles the validation of the email format before making the API call and 
	// displays appropriate error messages based on the validation and API response.
	checkEmailUnique() {
		const email = this.getTrimmedValue();
		const apiUrl = this.data?.checkEmailApi;

		if (!email) return;

		// Prevent API call if email already checked
		if (this.lastCheckedEmail === email) {
			if (this.lastEmailCheckResult === 'exists') {
				this.setFieldError(this.data.duplicateEmailErrorMessage);
				this.toggleNextButton(true);
			} else if (this.lastEmailCheckResult === 'success') {
				this.clearFieldError();
				this.toggleNextButton(false);
			}
			return;
		}

		// Converting regex string to RegExp
		const emailRegex = this.data?.regexPattern
			? new RegExp(this.data.regexPattern.replace(/^\/|\/$/g, ''))
			: null;

		checkInputAvailability({
			input: this.inputFieldElement,
			value: email,
			apiUrl: apiUrl,

			payloadBuilder: (val) => ({ emailId: val, householdId: this.data?.householdId }),

			validate: (val) => {
				if (!emailRegex) return true;
				return emailRegex.test(val);
			},

			onStart: () => {
				this.toggleNextButton(true);
			},

			onInvalid: () => {
				this.setFieldError(this.data.invalidErrorMessage);
				this.toggleNextButton(true);
			},

			onExists: () => {
				this.lastCheckedEmail = email;
				this.lastEmailCheckResult = 'exists';

				this.setFieldError(this.data.duplicateEmailErrorMessage);
				this.toggleNextButton(true);
			},

			onSuccess: () => {
				this.lastCheckedEmail = email;
				this.lastEmailCheckResult = 'success';

				this.clearFieldError();
				this.toggleNextButton(false);
			},

			onApiError: () => {
				this.setFieldError(this.data.apiCheckApiFailErrorMessage);
				this.toggleNextButton(true);
			}
		});
	}

	setFieldError(message) {
		const errorElement = SelectorEngine.findOne('.lta-text-input--error', this.inputFieldContainerElement);
		if (errorElement) {
			errorElement.innerHTML = message;
		}
		addInvalidClasses(this.inputFieldContainerElement, false);
	}

	clearFieldError() {
		const errorElement = SelectorEngine.findOne('.lta-text-input--error', this.inputFieldContainerElement);
		if (errorElement) {
			errorElement.innerHTML = '';
		}
		removeInvalidClasses(this.inputFieldContainerElement);
	}

	toggleNextButton(disable) {
		const nextButton = document.querySelector('.js-form-generator-next-button');
		if (nextButton) {
			nextButton.disabled = disable;
		}
	}

	validate() {
		const trimmedValue = this.getTrimmedValue();
		this.inputValue = trimmedValue;
		if (this.inputFieldElement) {
			this.inputFieldElement.value = trimmedValue;
		}

		const formElementData = {
			...(this.data?.hasLimit && {
				hasLimit: this.data?.hasLimit,
				charLimit: this.data?.charLimit
			}),
			...(this.data?.hasMaxLimit && {
				hasMaxLimit: this.data?.hasMaxLimit,
				maxCharLimit: this.data?.maxCharLimit
			}),
			type: this.data?.type,
			element: this.inputFieldContainerElement,
			formElement: this.inputFieldElement,
			errorMessage: this.data.errorMessage,
			isTextArea: this.data.isTextArea,
			minLength: this.data.minlength,
			minLengthErrorMessage: this.data.minLengthErrorMessage,
			maxCharErrorMessage: this.data.maxCharErrorMessage,
			allowNumbersOrSpecialCharacters: this.data?.allowNumbersOrSpecialCharacters,
			invalidErrorMessage: this.data?.invalidErrorMessage,
			inputType: InputTypes.SELECTOR_INPUT,
			hasIcon: false
		};

		return this.data?.isOptional ? true : validFormElementValue(trimmedValue, formElementData);
	}
}
export default InputField;
